/* eslint-disable */
import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { makeStyles } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import {
  ExpandMore,
  KeyboardArrowUp,
  KeyboardArrowDown,
  Remove,
} from "@material-ui/icons";

import {
  blendColors,
  whiteColor,
} from "assets/jss/material-dashboard-pro-react";

import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import Pnl from "components/Pnl/Pnl";
import { PnlSharedProvider } from "hooks/usePnlShared";

import alignmentSessionStyles from "./styles";
import useThemeColors from "hooks/useThemeColors";
import { usePnlConfig } from "./hooks";
// formatNumber removed - using formatMarginValue instead
import ParetoChart from "./ParetoChart";
import DonutChart from "./DonutChart";
import ClientCascadeTree from "./ClientCascadeTree";
import KeyMessagesPanel from "./KeyMessagesPanel";
import FocusModeWrapper from "./FocusModeWrapper";

// Helper function to determine ABCD group based on cumulative share
// prevCumulative = cumulative BEFORE this element was added
const getABCDGroup = (prevCumulative) => {
  if (prevCumulative < 20) return "A";
  if (prevCumulative < 50) return "B";
  if (prevCumulative < 80) return "C";
  return "D";
};

// Shared visual functions from centralized utils
import { getValueColor, getElementHighlightStyles } from "./utils";
import {
  getElementDetailData,
  getPnlValues,
  getCascadeDrilldownData,
} from "services/governanceService";
import CascadeDrilldownDialog from "./CascadeDrilldownDialog";

// Format percentage values - removes decimal for |value| >= 100
const formatPercentValue = (value, showSign = true) => {
  const absValue = Math.abs(value);
  const sign = showSign ? (value >= 0 ? "+" : "") : "";
  return absValue >= 100
    ? `${sign}${value.toFixed(0)}`
    : `${sign}${value.toFixed(1)}`;
};

// Format large numbers with magnitude suffix (k, M, B)
// Removes decimal for values >= 100 in the magnitude unit
const formatMarginValue = (value) => {
  const absValue = Math.abs(value);
  const sign = value >= 0 ? "+" : "";
  if (absValue >= 1000000000) {
    const scaled = value / 1000000000;
    return `${sign}${Math.abs(scaled) >= 100 ? scaled.toFixed(0) : scaled.toFixed(1)} B`;
  } else if (absValue >= 1000000) {
    const scaled = value / 1000000;
    return `${sign}${Math.abs(scaled) >= 100 ? scaled.toFixed(0) : scaled.toFixed(1)} M`;
  } else if (absValue >= 1000) {
    const scaled = value / 1000;
    return `${sign}${Math.abs(scaled) >= 100 ? scaled.toFixed(0) : scaled.toFixed(1)} k`;
  }
  return `${sign}${value.toFixed(0)}`;
};

// Helper function to get group color
const getGroupColor = (group, clientColor) => {
  switch (group) {
    case "A":
      return clientColor;
    case "B":
      return "#555";
    case "C":
      return "#999";
    case "D":
      return "#ccc";
    default:
      return "#999";
  }
};

const useStyles = makeStyles(alignmentSessionStyles);


/**
 * TopElementsSection component (generic)
 * Displays a top elements page with a selectable table and detail panel
 * Can be configured for different element types (clients, products, etc.)
 *
 * @param {string} sectionType - Type of section: "clients" or "products"
 * @param {string} donutChart1Title - Title for the first donut chart
 * @param {string} donutChart2Title - Title for the second donut chart
 * @param {string} periodReference - Selected period for data filtering
 * @param {string} clientColor - Theme color
 */
function TopElementsSection({
  sectionType = "clients",
  donutChart1Title,
  donutChart2Title,
  periodReference,
  clientColor: propsClientColor,
  activeSession,
  pnlMetadata,
  dataOrchestrator,
  highlightEnabled = false,
  focusMode = false,
}) {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const { clientColor: themeClientColor } = useThemeColors();
  const clientColor = propsClientColor || themeClientColor;

  // Translation namespace based on section type
  const translationNs =
    sectionType === "products" ? "top_products" : "top_clients";

  // ==================== Data from orchestrator ====================
  // The orchestrator loads data automatically — no on-demand trigger needed.
  const topData = dataOrchestrator?.getTopElementsData?.(sectionType) || null;
  const isDataLoading = !topData;

  // Derived data from orchestrator
  const rankingData = topData?.ranking || [];
  const cascadeData = topData?.cascade || null;
  const totalComparison = topData?.total_comparison || {};
  const companyMix = topData?.company_mix || {};

  // Total Companhia reference (dynamic from backend)
  const totalCompanhia = useMemo(
    () => ({
      name: "Total Companhia",
      share: 100.0,
      deltaVolume: totalComparison.deltaVolume || 0,
      deltaAvgPrice: totalComparison.deltaAvgPrice || 0,
      deltaRevenue: totalComparison.deltaRevenue || 0,
      deltaMargin: totalComparison.deltaMargin || 0,
      deltaMarginPct: totalComparison.deltaMarginPct || 0,
    }),
    [totalComparison],
  );
  // Backend tells us which dimension it used
  const resolvedPrimaryDimension = topData?.primary_dimension || null;

  // Mix keys: first two keys from company_mix
  const mixKeys = useMemo(
    () => Object.keys(companyMix).slice(0, 2),
    [companyMix],
  );

  // Get dimension names from dimension data (or metadata)
  const dimensionDataResponse = dataOrchestrator?.getDimensionData?.() || null;
  const dimensionNames = dimensionDataResponse?.dimension_names || {};

  // Build dimension options from available dimension data
  const dimensionOptions = useMemo(() => {
    if (!dimensionDataResponse?.dimensions) return [];
    return Object.keys(dimensionDataResponse.dimensions).map((key) => {
      const names = dimensionNames[key];
      const label =
        names && typeof names === "object"
          ? names[i18n.language] || names.en || key
          : names || key;
      return { value: key, label };
    });
  }, [dimensionDataResponse, dimensionNames, i18n.language]);

  // Default dimension: resolved by the backend (no frontend guessing)
  const defaultDimension = resolvedPrimaryDimension;

  // Selected dimension state
  const [selectedDimension, setSelectedDimension] = useState(null);

  // Auto-set default dimension
  useEffect(() => {
    if (!selectedDimension && defaultDimension) {
      setSelectedDimension(defaultDimension);
    }
  }, [selectedDimension, defaultDimension]);
  const [dimensionMenuOpen, setDimensionMenuOpen] = useState(false);
  const dimensionMenuRef = useRef(null);

  // State for key messages (will be replaced by API data)
  const [keyMessages, setKeyMessages] = useState([]);

  // Selected element state (client or other dimension element)
  const [selectedElementId, setSelectedElementId] = useState(null);

  // Element-specific detail data (fetched on-demand when element is clicked)
  const [elementMix, setElementMix] = useState(null);
  const [elementPnlValues, setElementPnlValues] = useState({});
  const [elementDetailLoading, setElementDetailLoading] = useState(false);

  // Cascade drilldown dialog state
  const [cascadeDialogOpen, setCascadeDialogOpen] = useState(false);
  const [cascadeDialogTitle, setCascadeDialogTitle] = useState("");
  const [cascadeElements, setCascadeElements] = useState(null);
  const [cascadeVariations, setCascadeVariations] = useState(null);
  const [cascadeTotalEntered, setCascadeTotalEntered] = useState(0);
  const [cascadeLoading, setCascadeLoading] = useState(false);

  // Use centralized P&L configuration
  const {
    plStructure,
    netRevenueAnchor,
    serveMarginAnchor,
    volumeUnit,
    volumeL12M,
    oom,
    staticPnlProps,
    drillDownsOpen,
    setDrillDownsOpen,
    mouseOverPLLine,
    mouseOverPLLineFade,
    timersAllowed,
    handleMouseOverPLLine,
    handleMouseLeavePLLine,
  } = usePnlConfig({
    translateStructure: true,
    pnlMetadata,
    plValues: elementPnlValues,
  });

  // Close dimension menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dimensionMenuRef.current &&
        !dimensionMenuRef.current.contains(event.target)
      ) {
        setDimensionMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Get dimension data based on selected dimension (with ABCD group)
  const dimensionData = useMemo(() => {
    let sortedData;

    // If the selected dimension matches the primary (client or product),
    // use the ranking data from the top-elements endpoint
    if (selectedDimension === defaultDimension) {
      sortedData = [...rankingData].sort((a, b) => b.share - a.share);
    } else {
      // For other dimensions, use dimension_data from orchestrator
      const dimResponse =
        dimensionDataResponse?.dimensions?.[selectedDimension];
      if (dimResponse && dimResponse.elements) {
        sortedData = dimResponse.elements
          .map((el, idx) => ({ ...el, id: idx + 1 }))
          .sort((a, b) => b.share - a.share);
      } else {
        return [];
      }
    }

    // Calculate cumulative share and assign ABCD group
    let cumulative = 0;
    return sortedData.map((el) => {
      const prevCumulative = cumulative;
      cumulative += el.share;
      return {
        ...el,
        cumulativeShare: cumulative,
        abcdGroup: getABCDGroup(prevCumulative),
      };
    });
  }, [selectedDimension, defaultDimension, rankingData, dimensionDataResponse]);

  // Get current dimension label (memoized)
  const currentDimensionLabel = useMemo(
    () =>
      dimensionOptions.find((d) => d.value === selectedDimension)?.label || "",
    [dimensionOptions, selectedDimension],
  );

  // Handle element row click (memoized)
  const handleElementClick = useCallback((elementId) => {
    setSelectedElementId((prev) => (prev === elementId ? null : elementId));
  }, []);

  // Map cascade node keys to display titles (matches backend node_titles)
  const cascadeNodeTitles = useMemo(
    () => ({
      root: "Total",
      gm_positive: "Mg B positiva",
      gm_negative: "Mg B negativa",
      sm_positive: "Mg S positiva",
      sm_negative: "Mg S negativa",
    }),
    [],
  );

  // Handle cascade node click — open drilldown dialog
  const handleCascadeNodeClick = useCallback(
    (cascadeNodeKey) => {
      setCascadeDialogTitle(
        cascadeNodeTitles[cascadeNodeKey] || cascadeNodeKey,
      );
      setCascadeDialogOpen(true);
      setCascadeElements(null);
      setCascadeVariations(null);
      setCascadeLoading(true);

      const compPeriods = dataOrchestrator?.getComparisonPeriods?.();
      if (!compPeriods) {
        setCascadeLoading(false);
        return;
      }

      getCascadeDrilldownData({
        element_type: sectionType,
        period_current: compPeriods.current,
        period_previous: compPeriods.previous,
        cascade_node: cascadeNodeKey,
      })
        .then((res) => {
          setCascadeDialogTitle(res.node_title || cascadeNodeKey);
          setCascadeElements(res.elements || []);
          setCascadeVariations(res.variations || []);
          setCascadeTotalEntered(res.total_entered || 0);
        })
        .catch(() => {
          setCascadeElements([]);
          setCascadeVariations([]);
        })
        .finally(() => {
          setCascadeLoading(false);
        });
    },
    [sectionType, dataOrchestrator],
  );

  const handleCascadeDialogClose = useCallback(() => {
    setCascadeDialogOpen(false);
  }, []);

  // Handle dimension change (memoized)
  const handleDimensionChange = useCallback((dimension) => {
    setSelectedDimension(dimension);
    setSelectedElementId(null); // Reset selection when dimension changes
    setDimensionMenuOpen(false);
  }, []);

  // Get selected element (memoized)
  const selectedElement = useMemo(
    () =>
      selectedElementId
        ? dimensionData.find((c) => c.id === selectedElementId)
        : null,
    [selectedElementId, dimensionData],
  );

  // Fetch element-specific detail data when an element is selected
  useEffect(() => {
    if (!selectedElement || !activeSession || !pnlMetadata) {
      setElementMix(null);
      setElementPnlValues({});
      return;
    }

    const topDataCheck = dataOrchestrator?.getTopElementsData?.(sectionType);
    if (!topDataCheck) return;

    const primaryDim = resolvedPrimaryDimension;
    if (!primaryDim || selectedDimension !== defaultDimension) {
      // Only fetch element detail for the primary dimension
      setElementMix(null);
      setElementPnlValues({});
      return;
    }

    let cancelled = false;
    setElementDetailLoading(true);

    // Use the orchestrator's comparison periods (supports all 8 types)
    const compPeriods = dataOrchestrator?.getComparisonPeriods?.();
    if (!compPeriods || !compPeriods.current?.length) {
      setElementDetailLoading(false);
      return;
    }

    const periodCurrent = compPeriods.current;
    const periodPrevious = compPeriods.previous;
    const currencyUnit = pnlMetadata?.pl?.default_currency_unit || "BRL";

    // Fetch element mix + element P&L in parallel
    Promise.all([
      // Element-specific mix via the same endpoint with element_name
      getElementDetailData({
        element_type: sectionType,
        period_current: periodCurrent,
        period_previous: periodPrevious,
        top_n: 1,
        element_name: selectedElement.name,
      }),
      // Element-specific P&L (current period filtered by dimension)
      getPnlValues({
        currency_unit: currencyUnit,
        common_filters: {
          period: periodCurrent,
          [primaryDim]: [selectedElement.name],
        },
        common_filters_mode: { period: "items", [primaryDim]: "items" },
        common_filters_sequence: ["period", primaryDim],
        pl_filters: {},
        pl_filters_mode: {},
        pl_filters_sequence: [],
      }),
      // Element-specific P&L (previous period filtered by dimension)
      getPnlValues({
        currency_unit: currencyUnit,
        common_filters: {
          period: periodPrevious,
          [primaryDim]: [selectedElement.name],
        },
        common_filters_mode: { period: "items", [primaryDim]: "items" },
        common_filters_sequence: ["period", primaryDim],
        pl_filters: {},
        pl_filters_mode: {},
        pl_filters_sequence: [],
      }),
    ])
      .then(([mixRes, plCurrRes, plPrevRes]) => {
        if (cancelled) return;
        setElementMix(mixRes?.element_mix || null);
        setElementPnlValues({
          1: plPrevRes?.pl || {},
          2: plCurrRes?.pl || {},
        });
        setElementDetailLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setElementMix(null);
        setElementPnlValues({});
        setElementDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    selectedElement,
    selectedDimension,
    defaultDimension,
    resolvedPrimaryDimension,
    activeSession,
    pnlMetadata,
    periodReference,
    sectionType,
    dataOrchestrator,
  ]);


  return (
    <FocusModeWrapper focusMode={focusMode}>
      <div
        className={
          focusMode
            ? classes.topClientsContainerFocus
            : classes.topClientsContainer
        }
        style={focusMode ? { height: "100%", marginTop: 0 } : {}}
      >
        <div
          className={
            focusMode
              ? classes.topClientsMainLayoutFocus
              : classes.topClientsMainLayout
          }
        >
          {/* Left Card - Clients Table */}
          <div className={classes.topClientsLeftCard}>
            <Card>
              <CardBody>
                <div className={classes.clientsTable}>
                  {/* Table Header */}
                  <div className={classes.clientsTableHeader}>
                    <div className={classes.clientsTableHeaderCell}>#</div>
                    <div
                      className={`${classes.clientsTableHeaderCell} ${classes.dimensionDropdownCell}`}
                      ref={dimensionMenuRef}
                    >
                      <div
                        className={classes.dimensionDropdownButton}
                        onClick={() => setDimensionMenuOpen(!dimensionMenuOpen)}
                      >
                        <span>{currentDimensionLabel}</span>
                        <ExpandMore
                          className={`${classes.dimensionDropdownIcon} ${
                            dimensionMenuOpen
                              ? classes.dimensionDropdownIconOpen
                              : ""
                          }`}
                        />
                      </div>
                      {dimensionMenuOpen && (
                        <div className={classes.dimensionDropdownMenu}>
                          {dimensionOptions.map((option) => (
                            <div
                              key={option.value}
                              className={`${classes.dimensionDropdownItem} ${
                                selectedDimension === option.value
                                  ? classes.dimensionDropdownItemActive
                                  : ""
                              }`}
                              onClick={() =>
                                handleDimensionChange(option.value)
                              }
                            >
                              {option.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className={classes.clientsTableHeaderCell}>
                      <span>
                        {t("alignment_session.table_headers.rep_pct")}
                      </span>
                      <span className={classes.dimensionTableHeaderUnit}>
                        %
                      </span>
                    </div>
                    <div className={classes.clientsTableHeaderCell}>
                      <span>
                        {t("alignment_session.table_headers.delta_vol")}
                      </span>
                      <span className={classes.dimensionTableHeaderUnit}>
                        %
                      </span>
                    </div>
                    <div className={classes.clientsTableHeaderCell}>
                      <span>
                        {t("alignment_session.table_headers.delta_price")}
                      </span>
                      <span className={classes.dimensionTableHeaderUnit}>
                        %
                      </span>
                    </div>
                    <div className={classes.clientsTableHeaderCell}>
                      <span>
                        {t("alignment_session.table_headers.delta_revenue")}
                      </span>
                      <span className={classes.dimensionTableHeaderUnit}>
                        %
                      </span>
                    </div>
                    <div className={classes.clientsTableHeaderCell}>
                      <span>
                        {t("alignment_session.table_headers.delta_margin")}
                      </span>
                      <span className={classes.dimensionTableHeaderUnit}>
                        BRL
                      </span>
                    </div>
                    <div className={classes.clientsTableHeaderCell}>
                      <span>
                        {t("alignment_session.table_headers.delta_margin_pct")}
                      </span>
                      <span className={classes.dimensionTableHeaderUnit}>
                        pp
                      </span>
                    </div>
                  </div>

                  {/* Table Body */}
                  <div className={classes.clientsTableBody}>
                    {/* Total Companhia Row */}
                    <div
                      className={`${classes.clientsTableRow} ${classes.dimensionTableRowTotal}`}
                    >
                      <div
                        className={`${classes.clientsTableCell} ${classes.clientRankingCell}`}
                      >
                        <span>-</span>
                      </div>
                      <div className={classes.clientsTableCell}>
                        <span className={classes.dimensionElementName}>
                          {t("alignment_session.by_dimension.total_company")}
                        </span>
                      </div>
                      <div className={classes.clientsTableCell}>
                        <span>
                          {formatPercentValue(totalCompanhia.share, false)}
                        </span>
                      </div>
                      <div className={classes.clientsTableCell}>
                        <span>
                          {formatPercentValue(totalCompanhia.deltaVolume)}
                        </span>
                      </div>
                      <div className={classes.clientsTableCell}>
                        <span>
                          {formatPercentValue(totalCompanhia.deltaAvgPrice)}
                        </span>
                      </div>
                      <div className={classes.clientsTableCell}>
                        <span>
                          {formatPercentValue(totalCompanhia.deltaRevenue)}
                        </span>
                      </div>
                      <div className={classes.clientsTableCell}>
                        <span>
                          {formatMarginValue(totalCompanhia.deltaMargin)}
                        </span>
                      </div>
                      <div className={classes.clientsTableCell}>
                        <span>
                          {formatPercentValue(totalCompanhia.deltaMarginPct)}
                        </span>
                      </div>
                    </div>

                    {/* Element Rows */}
                    {dimensionData.map((element, index) => {
                      const isSelected = selectedElementId === element.id;
                      const isFaded = selectedElementId !== null && !isSelected;
                      const ranking = index + 1;
                      const rankingChange = element.rankingChange || 0;

                      // Colors (fixed thresholds, consistent across tables)
                      const volumeColor = getValueColor(
                        element.deltaVolume,
                        totalCompanhia.deltaVolume,
                      );
                      const avgPriceColor = getValueColor(
                        element.deltaAvgPrice || 0,
                        totalCompanhia.deltaAvgPrice,
                      );
                      const revenueColor = getValueColor(
                        element.deltaRevenue,
                        totalCompanhia.deltaRevenue,
                      );
                      const marginPctColor = getValueColor(
                        element.deltaMarginPct,
                        totalCompanhia.deltaMarginPct,
                      );

                      // Highlight styles (centralized, share-amplified)
                      const hl = getElementHighlightStyles(
                        element,
                        totalCompanhia,
                        highlightEnabled,
                      );
                      const volumeStyles = hl.volume;
                      const avgPriceStyles = hl.avgPrice;
                      const revenueStyles = hl.revenue;
                      const marginStyles = hl.margin;
                      const marginPctStyles = hl.marginPct;

                      // Ranking change icon
                      const RankingIcon =
                        rankingChange > 0
                          ? KeyboardArrowUp
                          : rankingChange < 0
                            ? KeyboardArrowDown
                            : Remove;
                      const rankingIconClass =
                        rankingChange > 0
                          ? classes.rankingUp
                          : rankingChange < 0
                            ? classes.rankingDown
                            : classes.rankingNeutral;

                      return (
                        <div
                          key={element.id}
                          className={`${classes.clientsTableRow} ${
                            isFaded ? classes.clientsTableRowFaded : ""
                          } ${isSelected ? classes.clientsTableRowSelected : ""}`}
                          onClick={() => handleElementClick(element.id)}
                        >
                          <div
                            className={`${classes.clientsTableCell} ${classes.clientRankingCell}`}
                          >
                            <span>{ranking}</span>
                            <RankingIcon
                              className={`${classes.rankingChangeIcon} ${rankingIconClass}`}
                            />
                          </div>
                          <div className={classes.clientsTableCell}>
                            <div className={classes.clientNameWithGroup}>
                              <span
                                className={classes.clientGroupBadge}
                                style={{
                                  backgroundColor: getGroupColor(
                                    element.abcdGroup,
                                    clientColor,
                                  ),
                                }}
                              >
                                {element.abcdGroup}
                              </span>
                              <Tooltip
                                title={element.name}
                                placement="top-start"
                              >
                                <span className={classes.clientElementName}>
                                  {element.name}
                                </span>
                              </Tooltip>
                            </div>
                          </div>
                          <div className={classes.clientsTableCell}>
                            {/* % Rep - no color coding, same highlight as delta Mg $ */}
                            <span
                              className={classes.dimensionColoredValue}
                              style={{
                                opacity: marginStyles.opacity,
                                transform: `scale(${marginStyles.scale})`,
                              }}
                            >
                              {formatPercentValue(element.share, false)}
                            </span>
                          </div>
                          <div className={classes.clientsTableCell}>
                            <span
                              className={classes.dimensionColoredValue}
                              style={{
                                color: volumeColor,
                                opacity: volumeStyles.opacity,
                                transform: `scale(${volumeStyles.scale})`,
                              }}
                            >
                              {formatPercentValue(element.deltaVolume)}
                            </span>
                          </div>
                          <div className={classes.clientsTableCell}>
                            <span
                              className={classes.dimensionColoredValue}
                              style={{
                                color: avgPriceColor,
                                opacity: avgPriceStyles.opacity,
                                transform: `scale(${avgPriceStyles.scale})`,
                              }}
                            >
                              {formatPercentValue(element.deltaAvgPrice || 0)}
                            </span>
                          </div>
                          <div className={classes.clientsTableCell}>
                            <span
                              className={classes.dimensionColoredValue}
                              style={{
                                color: revenueColor,
                                opacity: revenueStyles.opacity,
                                transform: `scale(${revenueStyles.scale})`,
                              }}
                            >
                              {formatPercentValue(element.deltaRevenue)}
                            </span>
                          </div>
                          <div className={classes.clientsTableCell}>
                            {/* Delta Margin $ - no color coding */}
                            <span
                              className={classes.dimensionColoredValue}
                              style={{
                                opacity: marginStyles.opacity,
                                transform: `scale(${marginStyles.scale})`,
                              }}
                            >
                              {formatMarginValue(element.deltaMargin)}
                            </span>
                          </div>
                          <div className={classes.clientsTableCell}>
                            <span
                              className={classes.dimensionColoredValue}
                              style={{
                                color: marginPctColor,
                                opacity: marginPctStyles.opacity,
                                transform: `scale(${marginPctStyles.scale})`,
                              }}
                            >
                              {formatPercentValue(element.deltaMarginPct)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>

          {/* Right Side - Client Detail (4 cards) or Overview (two cards) */}
          {selectedElement ? (
            <div className={classes.clientDetailLayout}>
              {/* Card 1: Header with client name and share */}
              <div className={classes.clientHeaderCard}>
                <Card>
                  <CardBody>
                    <div className={classes.clientHeaderContent}>
                      <span className={classes.clientHeaderTitle}>
                        {selectedElement.name}
                      </span>
                      <span className={classes.clientHeaderShare}>
                        {selectedElement.share.toFixed(1)}%{" "}
                        {t(`alignment_session.${translationNs}.share_label`)}
                      </span>
                    </div>
                  </CardBody>
                </Card>
              </div>

              {/* Bottom row: Donut cards + P&L card */}
              <div className={classes.clientDetailBottomRow}>
                {/* Left column: 2 Donut cards */}
                <div className={classes.clientDonutColumn}>
                  {/* Card 2: First Donut (Categories or Channels) */}
                  <div className={classes.clientDonutCard}>
                    <Card>
                      <CardBody>
                        <div className={classes.donutChartContainer}>
                          <DonutChart
                            title={donutChart1Title}
                            subtitle={t(
                              `alignment_session.${translationNs}.donut_subtitle`,
                            )}
                            clientData={
                              elementMix?.[mixKeys[0]] ||
                              companyMix[mixKeys[0]] ||
                              []
                            }
                            companyData={companyMix[mixKeys[0]] || []}
                            innerRingLabel={
                              selectedElement?.name ||
                              t(
                                `alignment_session.${translationNs}.element_label`,
                              )
                            }
                            loading={elementDetailLoading}
                          />
                        </div>
                      </CardBody>
                    </Card>
                  </div>

                  {/* Card 3: Second Donut (Brands or Regions) */}
                  <div className={classes.clientDonutCard}>
                    <Card>
                      <CardBody>
                        <div className={classes.donutChartContainer}>
                          <DonutChart
                            title={donutChart2Title}
                            subtitle={t(
                              `alignment_session.${translationNs}.donut_subtitle`,
                            )}
                            clientData={
                              elementMix?.[mixKeys[1]] ||
                              companyMix[mixKeys[1]] ||
                              []
                            }
                            companyData={companyMix[mixKeys[1]] || []}
                            innerRingLabel={
                              selectedElement?.name ||
                              t(
                                `alignment_session.${translationNs}.element_label`,
                              )
                            }
                            loading={elementDetailLoading}
                          />
                        </div>
                      </CardBody>
                    </Card>
                  </div>
                </div>

                {/* Card 4: P&L */}
                <div className={classes.clientPnlCard}>
                  <Card style={{ backgroundColor: "transparent" }}>
                    <CardBody style={{ padding: "0" }}>
                      <div className={classes.clientPnlWrapper}>
                        <PnlSharedProvider
                          plValues={elementPnlValues}
                          plStructure={plStructure}
                          benchmark={null}
                          netRevenueAnchor={netRevenueAnchor}
                          serveMarginAnchor={serveMarginAnchor}
                          oom={oom}
                          t={t}
                        >
                          <div className={classes.plCardsRow}>
                            {/* P&L Names */}
                            <Card className={classes.plCardClient}>
                              <CardBody className={classes.plCardBody}>
                                <Pnl
                                  plStructure={plStructure}
                                  netRevenueAnchor={netRevenueAnchor}
                                  serveMarginAnchor={serveMarginAnchor}
                                  numPL={1}
                                  numPLs={2}
                                  plName={"-"}
                                  handleMouseLeavePLLine={
                                    handleMouseLeavePLLine
                                  }
                                  handleMouseOverPLLine={handleMouseOverPLLine}
                                  mouseOverPLLine={mouseOverPLLine}
                                  mouseOverPLLineFade={mouseOverPLLineFade}
                                  timersAllowed={timersAllowed.current}
                                  drillDownsOpen={drillDownsOpen}
                                  setDrillDownsOpen={setDrillDownsOpen}
                                  plValues={elementPnlValues}
                                  volumeUnit={volumeUnit}
                                  volumeL12M={volumeL12M}
                                  oom={oom}
                                  showColumns={["name"]}
                                  fixedHighlightLine={serveMarginAnchor.current}
                                  {...staticPnlProps}
                                  isRefreshing={elementDetailLoading}
                                />
                              </CardBody>
                            </Card>
                            {/* P&L Previous */}
                            <Card className={classes.plCardClient}>
                              <CardBody className={classes.plCardBody}>
                                <Pnl
                                  plStructure={plStructure}
                                  netRevenueAnchor={netRevenueAnchor}
                                  serveMarginAnchor={serveMarginAnchor}
                                  numPL={1}
                                  numPLs={2}
                                  plName={t(
                                    `alignment_session.${translationNs}.ytd_previous`,
                                  )}
                                  handleMouseLeavePLLine={
                                    handleMouseLeavePLLine
                                  }
                                  handleMouseOverPLLine={handleMouseOverPLLine}
                                  mouseOverPLLine={mouseOverPLLine}
                                  mouseOverPLLineFade={mouseOverPLLineFade}
                                  timersAllowed={timersAllowed.current}
                                  drillDownsOpen={drillDownsOpen}
                                  setDrillDownsOpen={setDrillDownsOpen}
                                  plValues={elementPnlValues}
                                  volumeUnit={volumeUnit}
                                  volumeL12M={volumeL12M}
                                  oom={oom}
                                  showColumns={["val", "percentage"]}
                                  fixedHighlightLine={serveMarginAnchor.current}
                                  {...staticPnlProps}
                                  isRefreshing={elementDetailLoading}
                                  plsToRefresh={elementDetailLoading ? [1] : []}
                                />
                              </CardBody>
                            </Card>
                            {/* P&L Current */}
                            <Card className={classes.plCardClient}>
                              <CardBody className={classes.plCardBody}>
                                <Pnl
                                  plStructure={plStructure}
                                  netRevenueAnchor={netRevenueAnchor}
                                  serveMarginAnchor={serveMarginAnchor}
                                  numPL={2}
                                  numPLs={2}
                                  plName={t(
                                    `alignment_session.${translationNs}.ytd_current`,
                                  )}
                                  handleMouseLeavePLLine={
                                    handleMouseLeavePLLine
                                  }
                                  handleMouseOverPLLine={handleMouseOverPLLine}
                                  mouseOverPLLine={mouseOverPLLine}
                                  mouseOverPLLineFade={mouseOverPLLineFade}
                                  timersAllowed={timersAllowed.current}
                                  drillDownsOpen={drillDownsOpen}
                                  setDrillDownsOpen={setDrillDownsOpen}
                                  plValues={elementPnlValues}
                                  volumeUnit={volumeUnit}
                                  volumeL12M={volumeL12M}
                                  oom={oom}
                                  showColumns={["val", "percentage"]}
                                  fixedHighlightLine={serveMarginAnchor.current}
                                  {...staticPnlProps}
                                  isRefreshing={elementDetailLoading}
                                  plsToRefresh={elementDetailLoading ? [2] : []}
                                />
                              </CardBody>
                            </Card>
                            {/* P&L Delta */}
                            <Card className={classes.plCardClient}>
                              <CardBody className={classes.plCardBody}>
                                <Pnl
                                  plStructure={plStructure}
                                  netRevenueAnchor={netRevenueAnchor}
                                  serveMarginAnchor={serveMarginAnchor}
                                  numPL={2}
                                  numPLs={2}
                                  plName={"Δ"}
                                  handleMouseLeavePLLine={
                                    handleMouseLeavePLLine
                                  }
                                  handleMouseOverPLLine={handleMouseOverPLLine}
                                  mouseOverPLLine={mouseOverPLLine}
                                  mouseOverPLLineFade={mouseOverPLLineFade}
                                  timersAllowed={timersAllowed.current}
                                  drillDownsOpen={drillDownsOpen}
                                  setDrillDownsOpen={setDrillDownsOpen}
                                  plValues={elementPnlValues}
                                  volumeUnit={volumeUnit}
                                  volumeL12M={volumeL12M}
                                  oom={oom}
                                  showColumns={["delta"]}
                                  showDelta={true}
                                  deltaBaseNumPL={1}
                                  fixedHighlightLine={serveMarginAnchor.current}
                                  {...staticPnlProps}
                                  isRefreshing={elementDetailLoading}
                                  plsToRefresh={elementDetailLoading ? [2] : []}
                                />
                              </CardBody>
                            </Card>
                          </div>
                        </PnlSharedProvider>
                      </div>
                    </CardBody>
                  </Card>
                </div>
              </div>
            </div>
          ) : (
            <div className={classes.topClientsRightColumn}>
              {/* Card 1: Pareto Chart */}
              <div className={classes.topClientsParetoCard}>
                <Card>
                  <div
                    className={classes.sectionTitleCardHeader}
                    style={{
                      borderBottom:
                        "1px solid " +
                        blendColors(clientColor, whiteColor, 0.25),
                    }}
                  >
                    {t("alignment_session.top_clients.concentration_chart")}
                  </div>
                  <CardBody style={{ padding: "0 !important" }}>
                    <div className={classes.paretoChartSection}>
                      <div className={classes.paretoChartWrapper}>
                        <ParetoChart
                          data={
                            selectedDimension !== defaultDimension
                              ? dimensionData
                              : null
                          }
                          preAggregatedData={
                            selectedDimension === defaultDimension
                              ? topData?.pareto_data
                              : null
                          }
                          clientColor={clientColor}
                        />
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </div>

              {/* Card 2: Cascade Tree */}
              <div className={classes.topClientsCascadeCard}>
                <Card>
                  <div
                    className={classes.sectionTitleCardHeader}
                    style={{
                      borderBottom:
                        "1px solid " +
                        blendColors(clientColor, whiteColor, 0.25),
                    }}
                  >
                    {t("alignment_session.top_clients.profitability_cascade") ||
                      "Cascata de Rentabilidade"}
                  </div>
                  <CardBody>
                    <div className={classes.cascadeTreeSection}>
                      <div className={classes.cascadeTreeWrapper}>
                        <ClientCascadeTree
                          data={cascadeData}
                          showEbitda={true}
                          elementLabel={
                            sectionType === "products"
                              ? t("alignment_session.cascade.products_label")
                              : t("alignment_session.cascade.clients_label")
                          }
                          onNodeClick={handleCascadeNodeClick}
                        />
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </div>
            </div>
          )}
        </div>

        {/* Key Messages Panel */}
        <KeyMessagesPanel
          messages={keyMessages}
          onMessagesChange={setKeyMessages}
        />

        {/* Cascade Drilldown Dialog */}
        <CascadeDrilldownDialog
          open={cascadeDialogOpen}
          onClose={handleCascadeDialogClose}
          clientColor={clientColor}
          nodeTitle={cascadeDialogTitle}
          elements={cascadeElements}
          variations={cascadeVariations}
          loading={cascadeLoading}
          totalEntered={cascadeTotalEntered}
          elementLabel={
            sectionType === "products"
              ? t("alignment_session.cascade.products_label") || "produtos"
              : t("alignment_session.cascade.clients_label") || "clientes"
          }
        />
      </div>
    </FocusModeWrapper>
  );
}

TopElementsSection.propTypes = {
  sectionType: PropTypes.oneOf(["clients", "products"]),
  donutChart1Title: PropTypes.string,
  donutChart2Title: PropTypes.string,
  periodReference: PropTypes.string,
  clientColor: PropTypes.string,
  highlightEnabled: PropTypes.bool,
  focusMode: PropTypes.bool,
};

TopElementsSection.defaultProps = {
  sectionType: "clients",
  donutChart1Title: "Mix de Categorias",
  donutChart2Title: "Mix de Marcas",
  periodReference: "ytd",
  clientColor: "#1976d2",
  highlightEnabled: false,
  focusMode: false,
};

/**
 * TopClientsSection - Wrapper for client-specific view
 */
function TopClientsSection(props) {
  return (
    <TopElementsSection
      {...props}
      sectionType="clients"
      donutChart1Title="Mix de Categorias"
      donutChart2Title="Mix de Marcas"
    />
  );
}

TopClientsSection.propTypes = {
  periodReference: PropTypes.string,
  clientColor: PropTypes.string,
  highlightEnabled: PropTypes.bool,
  focusMode: PropTypes.bool,
  activeSession: PropTypes.object,
  pnlMetadata: PropTypes.object,
  dataOrchestrator: PropTypes.object,
};

TopClientsSection.defaultProps = {
  focusMode: false,
};

/**
 * TopProductsSection - Wrapper for product-specific view
 */
function TopProductsSection(props) {
  return (
    <TopElementsSection
      {...props}
      sectionType="products"
      donutChart1Title="Mix de Canais"
      donutChart2Title="Mix de Regiões"
    />
  );
}

TopProductsSection.propTypes = {
  periodReference: PropTypes.string,
  clientColor: PropTypes.string,
  highlightEnabled: PropTypes.bool,
  focusMode: PropTypes.bool,
  activeSession: PropTypes.object,
  pnlMetadata: PropTypes.object,
  dataOrchestrator: PropTypes.object,
};

TopProductsSection.defaultProps = {
  focusMode: false,
};

export default TopClientsSection;
export { TopProductsSection };
